// components/pages/Members.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  Search,
  Users,
  User,
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Filter,
  Download,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  MapPin,
  Phone,
  Mail,
  Calendar,
  Trophy,
  Save,
  X,
  Plus,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  StickyNote,
  AlertTriangle,
  RefreshCw,
  Send,
  GraduationCap,
  } from 'lucide-react';
import AddMemberForm from '../layout/AddMemberForm';
import EmailTemplates from './EmailTemplates';
import {
  updateMember,
  deleteMember,
  getMemberById,
  batchDeleteMembers,
  getMembers,
  getParents,
} from '../../firebase/apis/api-parents';
import { getLocations } from '../../firebase/firestore';
import AdminAPI from '../../firebase/apis/api-admin';
import Header from '../layout/Header';
import Button from '../common/Button';
import Modal from '../common/Modal';
import Table, { TableRow, TableCell } from '../common/Table';
import CustomDatePicker from '../common/CustomDatePicker';
import { API_CONFIG, buildApiUrl, auth } from '../../firebase/config';
import { sendPasswordResetEmail } from 'firebase/auth';
import { GRADE_BANDS } from '../../utils/curriculumConstants';
import {
  GRADE_OPTIONS,
  GRADE_CHART_LABELS,
  expandMembersToStudentRows,
  filterStudentRows,
  getUniqueFilterValues,
  computeLocationStats,
  computeSitePerformanceTotals,
  computeGradeDistribution,
  computeMembershipBreakdown,
  getFollowUpItems,
  getLeadsCount,
  getContactedThisWeekCount,
  getMembershipTypeLabel,
} from '../../utils/memberAnalytics';

const SPORT_OPTIONS = ['Soccer', 'Flag Football', 'Cheer', 'Basketball'];
const gradeBandOptions = GRADE_BANDS.filter(b => b.value !== 'All Coaches');

// EditMemberForm Component
const EditMemberForm = ({ member, onSave, onCancel, loading }) => {
  const [locations, setLocations] = useState([]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    location: '',
    sport: '',
    membershipType: '',
    isPaidMember: false,
    students: []
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    const loadLocations = async () => {
      try {
        const data = await getLocations();
        setLocations(data || []);
      } catch (error) {
        console.error('Error loading locations:', error);
        setLocations([]);
      }
    };
    loadLocations();
  }, []);

  useEffect(() => {
    if (member) {
      setFormData({
        firstName: member.firstName || '',
        lastName: member.lastName || '',
        email: member.email || '',
        phone: member.phone || '',
        location: member.location || '',
        sport: member.sport || '',
        membershipType: member.membershipType || '',
        isPaidMember: member.isPaidMember || false,
        students: member.students || []
      });
    }
  }, [member]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName?.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName?.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (formData.phone && !/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Phone must be 10 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const updatedData = {
      ...formData,
      phone: formData.phone ? formData.phone.replace(/\D/g, '') : null
    };

    onSave(updatedData);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const addStudent = () => {
    setFormData(prev => ({
      ...prev,
      students: [...prev.students, {
        firstName: '',
        lastName: '',
        gradeBand: ''
      }]
    }));
  };

  const updateStudent = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      students: prev.students.map((student, i) =>
        i === index ? { ...student, [field]: value } : student
      )
    }));
  };

  const removeStudent = (index) => {
    setFormData(prev => ({
      ...prev,
      students: prev.students.filter((_, i) => i !== index)
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Information */}
      <div className="space-y-4">
        <h4 className="text-md font-semibold text-gray-800 border-b pb-2">
          Basic Information
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <User size={14} className="inline mr-1" />
              First Name *
            </label>
            <input
              type="text"
              value={formData.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.firstName ? 'border-red-300' : 'border-gray-300'
                }`}
              placeholder="First Name"
            />
            {errors.firstName && (
              <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Last Name *
            </label>
            <input
              type="text"
              value={formData.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.lastName ? 'border-red-300' : 'border-gray-300'
                }`}
              placeholder="Last Name"
            />
            {errors.lastName && (
              <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Mail size={14} className="inline mr-1" />
              Email *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.email ? 'border-red-300' : 'border-gray-300'
                }`}
              placeholder="Email Address"
            />
            {errors.email && (
              <p className="text-red-500 text-xs mt-1">{errors.email}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Phone size={14} className="inline mr-1" />
              Phone
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.phone ? 'border-red-300' : 'border-gray-300'
                }`}
              placeholder="Phone Number"
            />
            {errors.phone && (
              <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <MapPin size={14} className="inline mr-1" />
              Location
            </label>
            <select
              value={formData.location}
              onChange={(e) => handleInputChange('location', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Location</option>
              {locations.map((loc) => (
                <option key={loc.id || loc.name} value={loc.name || loc.label || loc}>
                  {loc.name || loc.label || loc}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Trophy size={14} className="inline mr-1" />
              Sport
            </label>
            <select
              value={formData.sport}
              onChange={(e) => handleInputChange('sport', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Sport</option>
              {SPORT_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Membership Type
            </label>
            <select
              value={formData.membershipType}
              onChange={(e) => handleInputChange('membershipType', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Type</option>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
              <option value="one-time">Seasonal</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payment Status
            </label>
            <div className="flex items-center space-x-4 mt-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="isPaidMember"
                  checked={formData.isPaidMember === true}
                  onChange={() => handleInputChange('isPaidMember', true)}
                  className="mr-2"
                />
                Paid Member
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="isPaidMember"
                  checked={formData.isPaidMember === false}
                  onChange={() => handleInputChange('isPaidMember', false)}
                  className="mr-2"
                />
                Free Member
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Students Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-md font-semibold text-gray-800 border-b pb-2">
            <Users size={16} className="inline mr-1" />
            Students ({formData.students.length})
          </h4>
          <Button
            type="button"
            onClick={addStudent}
            variant="secondary"
            size="sm"
            className="flex items-center gap-1"
          >
            <Plus size={14} />
            Add Student
          </Button>
        </div>

        {formData.students.map((student, index) => (
          <div key={index} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3 bg-white">
              <span className="font-medium text-gray-800">Student {index + 1}</span>
              <button
                type="button"
                onClick={() => removeStudent(index)}
                className="text-red-600 hover:text-red-800"
                title="Remove Student"
              >
                <X size={16} />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input
                type="text"
                value={student.firstName || ''}
                onChange={(e) => updateStudent(index, 'firstName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="First Name"
              />
              <input
                type="text"
                value={student.lastName || ''}
                onChange={(e) => updateStudent(index, 'lastName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Last Name"
              />
              <select
                value={student.gradeBand || ''}
                onChange={(e) => updateStudent(index, 'gradeBand', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Grade Band</option>
                {gradeBandOptions.map((band) => (
                  <option key={band.value} value={band.value}>{band.label}</option>
                ))}
              </select>
              <div className="flex items-center text-sm text-gray-500">
                <GraduationCap size={14} className="mr-1" />
                {student.gradeBand || 'No grade band set'}
              </div>
            </div>
          </div>
        ))}

        {formData.students.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <Users size={32} className="mx-auto mb-2 text-gray-300" />
            <p>No students added yet</p>
            <p className="text-sm">Click "Add Student" to get started</p>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
        <Button
          type="button"
          onClick={onCancel}
          variant="secondary"
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={loading}
          className="flex items-center gap-2"
        >
          <Save size={16} />
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </form>
  );
};

const DEFAULT_FILTERS = {
  location: 'all',
  grade: 'all',
  gradeBand: 'all',
  sport: 'all',
  membershipType: 'all',
  paymentStatus: 'all',
  registrationStatus: 'all',
  source: 'all',
  dateRegistered: '',
};

const StatusBadge = ({ status, type = 'payment' }) => {
  const styles = {
    payment: {
      Paid: 'bg-green-100 text-green-700',
      Pending: 'bg-orange-100 text-orange-700',
      Unpaid: 'bg-red-100 text-red-700',
    },
    registration: {
      Active: 'bg-green-100 text-green-700',
      Pending: 'bg-orange-100 text-orange-700',
      Inactive: 'bg-gray-100 text-gray-600',
    },
    followUp: {
      'Follow-up': 'bg-orange-100 text-orange-700',
      Contacted: 'bg-green-100 text-green-700',
    },
  };

  const className = styles[type]?.[status] || 'bg-gray-100 text-gray-600';

  return (
    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${className}`}>
      {status}
    </span>
  );
};

const GradeMiniChart = ({ gradeCounts = {} }) => {
  const values = GRADE_OPTIONS.map(g => gradeCounts[g] || 0);
  const max = Math.max(...values, 1);
  const maxBarPx = 72;

  return (
    <div className="flex items-end gap-0.5 h-24 mt-3">
      {GRADE_OPTIONS.map((grade, i) => {
        const count = values[i];
        const barHeightPx = count > 0
          ? Math.max(Math.round((count / max) * maxBarPx), 14)
          : 4;
        return (
          <div key={grade} className="flex-1 flex flex-col items-center self-stretch justify-end group" title={`${grade}: ${count} student${count !== 1 ? 's' : ''}`}>
            <span className="text-[11px] font-bold text-gray-700 leading-none mb-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
              {count > 0 ? count : ''}
            </span>
            <div
              className={`w-full rounded-sm ${count > 0 ? 'bg-gradient-to-t from-primary-600 to-primary-500' : 'bg-gray-200'}`}
              style={{ height: `${barHeightPx}px`, minHeight: count > 0 ? '14px' : '4px' }}
            />
          </div>
        );
      })}
    </div>
  );
};

const LocationInsightCard = ({ stats }) => {
  const percent = stats.percentToGoal || 0;
  const progressColor = percent >= 75 ? 'text-green-600' : percent >= 50 ? 'text-orange-500' : 'text-red-500';

  return (
    <div className="min-w-[240px] flex-1 bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-2">
        <MapPin size={14} className="text-primary-500" />
        <h4 className="font-semibold text-gray-800 text-sm truncate">{stats.location}</h4>
      </div>
      <div className="flex items-baseline gap-1 mb-1">
        <span className="text-xl font-bold text-gray-900">{stats.students}</span>
        <span className="text-sm text-gray-500">/ {stats.goal} Students</span>
      </div>
      <div className={`text-sm font-semibold mb-3 ${progressColor}`}>{percent}% to goal</div>
      <div className="w-full bg-gray-100 rounded-full h-2 mb-3">
        <div
          className="bg-gradient-to-r from-primary-500 to-secondary-500 h-2 rounded-full transition-all"
          style={{ width: `${Math.min(percent, 100)}%` }}
        />
      </div>
      <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-xs text-gray-600 mb-2">
        <span><strong className="text-green-600">{stats.active}</strong> Active</span>
        <span><strong className="text-orange-500">{stats.pending}</strong> Pending</span>
        <span><strong className="text-red-500">{stats.unpaid}</strong> Unpaid</span>
        <span><strong className="text-blue-600">{stats.monthly}</strong> Monthly</span>
        <span><strong className="text-purple-600">{stats.seasonal}</strong> Seasonal</span>
      </div>
      <GradeMiniChart gradeCounts={stats.gradeCounts} />
      <div className="flex justify-between text-xs text-gray-500 mt-1.5 px-0.5 font-medium">
        {['K', '1', '2', '3', '4', '5', '6', '7', '8'].map((g) => (
          <span key={g}>{g}</span>
        ))}
      </div>
    </div>
  );
};

const RegistrationByGradeChart = ({ gradeCounts, totalStudents, locationFilter, locations, onLocationChange }) => {
  const max = Math.max(...Object.values(gradeCounts), 1);

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <h3 className="text-base font-semibold text-gray-800">Registration by Grade (All Locations)</h3>
        <select
          value={locationFilter}
          onChange={(e) => onLocationChange(e.target.value)}
          className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value="all">All Locations</option>
          {locations.map((loc) => (
            <option key={loc.id} value={loc.name}>{loc.name}</option>
          ))}
        </select>
      </div>
      <div className="flex items-end gap-2 h-40">
        {GRADE_CHART_LABELS.map((grade) => {
          const count = gradeCounts[grade] || 0;
          const height = (count / max) * 100;
          return (
            <div key={grade} className="flex-1 flex flex-col items-center gap-1">
              <span className="text-xs font-medium text-gray-700">{count}</span>
              <div className="w-full flex items-end justify-center h-28">
                <div
                  className="w-full max-w-[32px] bg-gradient-to-t from-primary-600 to-primary-400 rounded-t-md transition-all"
                  style={{ height: `${Math.max(height, count > 0 ? 8 : 2)}%` }}
                />
              </div>
              <span className="text-xs text-gray-500">{grade === 'K' ? 'K' : `${grade}`}</span>
            </div>
          );
        })}
      </div>
      <p className="text-sm text-gray-500 mt-3 text-center">Total Students: {totalStudents}</p>
    </div>
  );
};

const MembershipDonutChart = ({ breakdown }) => {
  const { monthly, seasonal, unpaidPending, monthlyPercent, seasonalPercent, unpaidPendingPercent, total } = breakdown;

  const segments = [
    { label: 'Monthly', count: monthly, percent: monthlyPercent, color: '#3b82f6' },
    { label: 'Seasonal', count: seasonal, percent: seasonalPercent, color: '#10b981' },
    { label: 'Unpaid/Pending', count: unpaidPending, percent: unpaidPendingPercent, color: '#f97316' },
  ];

  let cumulative = 0;
  const gradientStops = segments
    .filter((s) => s.percent > 0)
    .map((s) => {
      const start = cumulative;
      cumulative += s.percent;
      return `${s.color} ${start}% ${cumulative}%`;
    })
    .join(', ');

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5">
      <h3 className="text-base font-semibold text-gray-800 mb-9">Membership Breakdown</h3>
      <div className="flex flex-col sm:flex-row items-center gap-6">
        <div className="relative w-40 h-40 flex-shrink-0">
          <div
            className="w-full h-full rounded-full"
            style={{
              background: gradientStops
                ? `conic-gradient(${gradientStops})`
                : '#e5e7eb',
            }}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-[84px] h-[84px] bg-white rounded-full flex items-center justify-center">
              <span className="text-lg font-bold text-gray-800">{total}</span>
            </div>
          </div>
        </div>
        <div className="space-y-3 flex-1">
          {segments.map((seg) => (
            <div key={seg.label} className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: seg.color }} />
                <span className="text-gray-700">{seg.label}</span>
              </div>
              <span className="font-medium text-gray-900">{seg.count} ({seg.percent}%)</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const FollowUpModal = ({ member, staffOptions, onSave, onClose, loading }) => {
  const [form, setForm] = useState({
    followUpNeeded: false,
    followUpDate: '',
    followUpAssignedTo: '',
    followUpNotes: '',
    lastContacted: '',
  });

  useEffect(() => {
    if (member) {
      setForm({
        followUpNeeded: Boolean(member.followUpNeeded),
        followUpDate: member.followUpDate || '',
        followUpAssignedTo: member.followUpAssignedTo || '',
        followUpNotes: member.followUpNotes || '',
        lastContacted: member.lastContacted || '',
      });
    }
  }, [member]);

  const handleMarkContacted = () => {
    onSave({
      ...form,
      followUpNeeded: false,
      lastContacted: new Date().toISOString(),
    });
  };

  return (
    <div className="space-y-4">
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={form.followUpNeeded}
          onChange={(e) => setForm({ ...form, followUpNeeded: e.target.checked })}
          className="rounded border-gray-300 text-primary-500"
        />
        <span className="text-sm font-medium text-gray-700">Follow-Up Needed</span>
      </label>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Follow-Up Date</label>
          <CustomDatePicker
            value={form.followUpDate}
            onChange={(val) => setForm({ ...form, followUpDate: val })}
            placeholder="Select date"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
          <select
            value={form.followUpAssignedTo}
            onChange={(e) => setForm({ ...form, followUpAssignedTo: e.target.value })}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
          >
            <option value="">Unassigned</option>
            {staffOptions.map((staff) => (
              <option key={staff.id || staff.email} value={staff.name || `${staff.firstName} ${staff.lastName}`}>
                {staff.name || `${staff.firstName} ${staff.lastName}`}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
        <textarea
          value={form.followUpNotes}
          onChange={(e) => setForm({ ...form, followUpNotes: e.target.value })}
          rows={4}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
          placeholder="Add follow-up notes..."
        />
      </div>

      {form.lastContacted && (
        <p className="text-sm text-gray-500">
          Last contacted: {new Date(form.lastContacted).toLocaleDateString()}
        </p>
      )}

      <div className="flex flex-wrap justify-end gap-2 pt-4 border-t">
        <Button variant="secondary" onClick={onClose} disabled={loading}>Cancel</Button>
        <Button variant="secondary" onClick={handleMarkContacted} disabled={loading}>
          Mark Contacted
        </Button>
        <Button onClick={() => onSave(form)} disabled={loading}>
          {loading ? 'Saving...' : 'Save Follow-Up'}
        </Button>
      </div>
    </div>
  );
};

const Members = () => {
  const [members, setMembers] = useState([]);
  const [parents, setParents] = useState([]);
  const [locations, setLocations] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedMember, setSelectedMember] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [gradeChartLocation, setGradeChartLocation] = useState('all');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState({ isOpen: false, member: null });
  const [showEmailTemplates, setShowEmailTemplates] = useState(false);
  const [followUpMember, setFollowUpMember] = useState(null);
  const [isFollowUpModalOpen, setIsFollowUpModalOpen] = useState(false);

  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [resetSending, setResetSending] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const itemsPerPageOptions = [5, 10, 25, 50, 100];

  const studentRows = useMemo(() => expandMembersToStudentRows(members), [members]);
  const rawFilterOptions = useMemo(
    () => getUniqueFilterValues(members, studentRows),
    [members, studentRows]
  );
  const filterOptions = useMemo(() => ({
    ...rawFilterOptions,
    sports: rawFilterOptions.sports.filter(s => s !== 'Baseball' && s !== 'Tackle_Football' && s !== 'TACKLE_FOOTBOLL' && s !== 'TACKLE_FOOTBALL'),
    membershipTypes: rawFilterOptions.membershipTypes.filter(t => t !== 'Free'),
  }), [rawFilterOptions]);
  const filteredStudentRows = useMemo(
    () => filterStudentRows(studentRows, searchTerm, filters),
    [studentRows, searchTerm, filters]
  );
  const locationStats = useMemo(
    () => computeLocationStats(members, locations),
    [members, locations]
  );
  const sitePerformanceTotals = useMemo(
    () => computeSitePerformanceTotals(locationStats),
    [locationStats]
  );
  const gradeDistribution = useMemo(
    () => computeGradeDistribution(studentRows, gradeChartLocation),
    [studentRows, gradeChartLocation]
  );
  const membershipBreakdown = useMemo(
    () => computeMembershipBreakdown(members),
    [members]
  );
  const followUpItems = useMemo(() => getFollowUpItems(members), [members]);
  const followUpsNeededCount = members.filter((m) => m.followUpNeeded).length;
  const contactedThisWeekCount = useMemo(() => getContactedThisWeekCount(members), [members]);
  const leadsCount = useMemo(() => getLeadsCount(parents, members), [parents, members]);

  // Sport options with emojis
  const getSportIcon = (sport) => {
    const icons = {
      'soccer': '⚽',
      'basketball': '🏀',
      'flag football': '🏈',
      'cheer': '📣'
    };
    return icons[sport?.toLowerCase()] || '🏆';
  };

  // Format date as "August 1, 2025"
  const formatDate = (date) => {
    if (!date) return 'N/A';

    let dateObj;
    if (date.toDate) {
      dateObj = date.toDate();
    } else {
      dateObj = new Date(date);
    }

    if (isNaN(dateObj.getTime())) return 'N/A';

    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return dateObj.toLocaleDateString('en-US', options);
  };

  useEffect(() => {
    loadAllData();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters]);

  const loadAllData = async () => {
    try {
      setLoading(true);
      const [membersData, locationsData, parentsData, adminsData] = await Promise.all([
        getMembers(),
        getLocations(),
        getParents().catch(() => []),
        AdminAPI.getAdmins().catch(() => ({ admins: [] })),
      ]);
      setMembers(membersData || []);
      setLocations(locationsData || []);
      setParents(parentsData || []);
      setStaffList(adminsData?.admins || []);
    } catch (error) {
      console.error('Error loading members data:', error);
      alert('Error loading members: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadMembers = loadAllData;

  const handleSaveFollowUp = async (followUpData) => {
    if (!followUpMember) return;
    try {
      setLoading(true);
      await updateMember(followUpMember.id, followUpData);
      setIsFollowUpModalOpen(false);
      setFollowUpMember(null);
      await loadAllData();
    } catch (error) {
      console.error('Error saving follow-up:', error);
      alert('Error saving follow-up: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const openFollowUp = (member) => {
    setFollowUpMember(member);
    setIsFollowUpModalOpen(true);
  };

  const formatPhoneLink = (phone) => {
    if (!phone) return 'N/A';
    const digits = phone.replace(/\D/g, '');
    const formatted = digits.length === 10
      ? `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
      : phone;
    return formatted;
  };

  // Enhanced delete function
  const handleDeleteMember = async (member) => {
    setDeleteConfirmation({ isOpen: true, member });
  };

  const confirmDelete = async () => {
    const { member } = deleteConfirmation;

    try {
      setLoading(true);
      const result = await deleteMember(member.id);

      if (result.success) {
        alert(`✅ ${result.message}`);
        loadMembers();
      } else {
        alert('❌ Failed to delete member');
      }
    } catch (error) {
      console.error('Error deleting member:', error);
      alert('❌ Error deleting member: ' + error.message);
    } finally {
      setLoading(false);
      setDeleteConfirmation({ isOpen: false, member: null });
    }
  };

  // Batch delete function
  const handleBatchDelete = async () => {
    if (selectedMembers.length === 0) {
      alert('Please select members to delete');
      return;
    }

    const confirmMessage = `Are you sure you want to delete ${selectedMembers.length} member(s)? This action cannot be undone and will remove them from all systems including Firebase Auth.`;

    if (!window.confirm(confirmMessage)) return;

    try {
      setLoading(true);
      const result = await batchDeleteMembers(selectedMembers);

      if (result.success) {
        alert(`✅ Successfully deleted ${result.summary.successful} member(s)`);
      } else {
        alert(`⚠️ Batch deletion completed with ${result.summary.failed} errors. Check console for details.`);
        console.log('Batch deletion results:', result.results);
      }

      setSelectedMembers([]);
      loadMembers();
    } catch (error) {
      console.error('Error in batch deletion:', error);
      alert('❌ Error during batch deletion: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Edit member function
  const handleEditMember = async (member) => {
    try {
      const memberData = await getMemberById(member.id);
      if (memberData) {
        setEditingMember(memberData);
        setIsEditModalOpen(true);
      }
    } catch (error) {
      console.error('Error loading member for edit:', error);
      alert('Error loading member data');
    }
  };

  const handleSaveEdit = async (updatedData) => {
    try {
      setLoading(true);
      await updateMember(editingMember.id, updatedData);
      alert('✅ Member updated successfully');
      setIsEditModalOpen(false);
      setEditingMember(null);
      loadMembers();
    } catch (error) {
      console.error('Error updating member:', error);
      alert('❌ Error updating member: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Add new member with Firebase Auth creation
  const  handleAddNewMember = async (memberData) => {
    try {
      setLoading(true);

      const newMemberData = {
        ...memberData,
        registrationSource: 'admin',
        createdBy: 'admin',
        isPaidMember:false,
        createdAt: new Date().toISOString(),
        uid: null,
      };

      const response = await fetch(buildApiUrl(API_CONFIG.endpoints.members.create), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newMemberData),
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to create member');
      }

      alert('✅ Member created successfully with login credentials');
      setIsAddMemberModalOpen(false);
      loadMembers();
    } catch (error) {
      console.error('Error creating member:', error);
      alert('❌ Error creating member: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSendResetLink = async (user) => {
    try {
      setResetSending(true);
      await sendPasswordResetEmail(auth, user.email);
      setResetSent(true);
      setTimeout(() => {
        setIsResetPasswordModalOpen(false);
        setResetSent(false);
        setResetPasswordUser(null);
      }, 3000);
    } catch (error) {
      console.error('Error sending password reset:', error);
      alert(`Failed to send reset email: ${error.message}`);
    } finally {
      setResetSending(false);
    }
  };

  const handleOpenAddMember = () => {
    setIsAddMemberModalOpen(true);
  };

  const handleViewDetails = (member) => {
    console.log("Viewing_member", member);
    setSelectedMember(member);
    setIsViewModalOpen(true);
  };

  const exportToCSV = () => {
    const headers = [
      'Student Name', 'Grade', 'Parent Name', 'Phone', 'Email', 'Location',
      'Sport', 'Membership Type', 'Payment Status', 'Reg. Status', 'Source',
      'Date Registered', 'Last Contacted',
    ];
    const csvContent = [
      headers.join(','),
      ...filteredStudentRows.map((row) => [
        `"${row.studentName}"`,
        row.grade,
        `"${row.parentName}"`,
        row.phone || '',
        row.email,
        row.location || '',
        row.sport || '',
        row.membershipType || '',
        row.paymentStatus,
        row.registrationStatus,
        row.source,
        formatDate(row.dateRegistered),
        row.lastContacted ? formatDate(row.lastContacted) : '',
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `members_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const clearFilters = () => setFilters(DEFAULT_FILTERS);

  const hasActiveFilters = Object.entries(filters).some(([key, value]) => {
    if (key === 'dateRegistered') return Boolean(value);
    return value !== 'all';
  });

  const totalItems = filteredStudentRows.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredStudentRows.slice(startIndex, endIndex);
  const currentPageMemberIds = [...new Set(currentItems.map((row) => row.memberId))];

  const goToFirstPage = () => setCurrentPage(1);
  const goToLastPage = () => setCurrentPage(totalPages);
  const goToPreviousPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));
  const goToNextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));

  const getPageNumbers = () => {
    const delta = 2;
    const pages = [];
    const startPage = Math.max(1, currentPage - delta);
    const endPage = Math.min(totalPages, currentPage + delta);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  if (loading && members.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div>
      <Header
        title="Members"
        subtitle="Real-time view of evening activity locations and member outreach"
      />

   {/* Page Actions */}
<div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
  <div className="text-sm text-white/60">
    Members &gt; All Members
  </div>
  <div className="flex flex-wrap gap-3">
    <Button onClick={handleOpenAddMember} className="flex items-center gap-2 bg-white text-purple-700 hover:bg-white/90 border-0 shadow-sm font-medium">
      <Plus size={16} />
      Add New Member
    </Button>
    <Button
      onClick={exportToCSV}
      variant="outline"
      className="flex items-center gap-2 border-white/30 text-white hover:bg-white/10 bg-transparent backdrop-blur-sm"
      disabled={filteredStudentRows.length === 0}
    >
      <Download size={16} />
      Export
    </Button>
    <Button
      onClick={() => setShowEmailTemplates(true)}
      className="flex items-center gap-2 border-white/30 text-white hover:bg-white/10 bg-transparent backdrop-blur-sm border"
    >
      <Mail size={16} />
      Email Templates
    </Button>
  </div>
</div>

{/* Global Search */}
<div className="relative mb-6">
  <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-purple-400" />
  <input
    type="text"
    placeholder="Search by student name, parent name, phone, email..."
    value={searchTerm}
    onChange={(e) => setSearchTerm(e.target.value)}
    className="w-full pl-12 pr-4 py-3.5 rounded-xl focus:outline-none 
      focus:ring-2 focus:ring-purple-300 focus:ring-offset-0
      bg-white/95 backdrop-blur-md border border-white/60
      text-gray-700 placeholder-gray-400
      shadow-lg shadow-purple-900/20
      transition-all duration-200"
  />
</div>

{/* Location Insight Cards */}
<div className="mb-6">
  <div className="flex items-center justify-between mb-3">
    <h3 className="text-base font-semibold text-white">Location Overview</h3>
    <button
      type="button"
      onClick={() => document.getElementById('site-performance-dashboard')?.scrollIntoView({ behavior: 'smooth' })}
      className="flex items-center gap-1 text-sm text-white/60 hover:text-white font-medium transition-colors"
    >
      View Site Performance Dashboard
      <ChevronRight size={16} />
    </button>
  </div>
  <div className="flex gap-4 overflow-x-auto pb-2">
    {locationStats.map((stats) => (
      <LocationInsightCard key={stats.location} stats={stats} />
    ))}
    {locationStats.length === 0 && !loading && (
      <div className="text-sm text-white/50 p-4">
        No locations configured. Add locations in the Locations section.
      </div>
    )}
  </div>
</div>

      <div className="glass rounded-2xl p-6">
        {/* Filters */}
        <div className="bg-gray-50 rounded-xl p-4 mb-6 border border-gray-100">
          <button
            type="button"
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            className="flex items-center justify-between w-full mb-3"
          >
            <div className="flex items-center gap-2">
              <Filter size={16} className="text-gray-600" />
              <span className="font-semibold text-gray-700 text-sm tracking-wide">FILTERS</span>
            </div>
            {filtersExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>

          {filtersExpanded && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Location</label>
                  <select
                    value={filters.location}
                    onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 bg-white"
                  >
                    <option value="all">All Locations</option>
                    {locations.map((loc) => (
                      <option key={loc.id} value={loc.name}>{loc.name}</option>
                    ))}
                  </select>
                </div>

                {/* <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Grade</label>
                  <select
                    value={filters.grade}
                    onChange={(e) => setFilters({ ...filters, grade: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 bg-white"
                  >
                    <option value="all">All Grades</option>
                    {GRADE_OPTIONS.map((grade) => (
                      <option key={grade} value={grade}>{grade}</option>
                    ))}
                  </select>
                </div> */}

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Grade Band</label>
                  <select
                    value={filters.gradeBand}
                    onChange={(e) => setFilters({ ...filters, gradeBand: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 bg-white"
                  >
                    <option value="all">All Grade Bands</option>
                    {gradeBandOptions.map((band) => (
                      <option key={band.value} value={band.value}>{band.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Sport / Activity</label>
                  <select
                    value={filters.sport}
                    onChange={(e) => setFilters({ ...filters, sport: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 bg-white"
                  >
                    <option value="all">All Sports</option>
                    {filterOptions.sports.map((sport) => (
                      <option key={sport} value={sport}>{sport}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Membership Type</label>
                  <select
                    value={filters.membershipType}
                    onChange={(e) => setFilters({ ...filters, membershipType: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 bg-white"
                  >
                    <option value="all">All Types</option>
                    {filterOptions.membershipTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Payment Status</label>
                  <select
                    value={filters.paymentStatus}
                    onChange={(e) => setFilters({ ...filters, paymentStatus: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 bg-white"
                  >
                    <option value="all">All</option>
                    <option value="Paid">Paid</option>
                    <option value="Unpaid">Unpaid</option>
                    <option value="Pending">Pending</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Registration Status</label>
                  <select
                    value={filters.registrationStatus}
                    onChange={(e) => setFilters({ ...filters, registrationStatus: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 bg-white"
                  >
                    <option value="all">All</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Pending">Pending</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Source</label>
                  <select
                    value={filters.source}
                    onChange={(e) => setFilters({ ...filters, source: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 bg-white"
                  >
                    <option value="all">All Sources</option>
                    {filterOptions.sources.map((source) => (
                      <option key={source} value={source}>{source}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Date Registered</label>
                  <CustomDatePicker
                    value={filters.dateRegistered}
                    onChange={(val) => setFilters({ ...filters, dateRegistered: val })}
                    placeholder="Select date"
                  />
                </div>
              </div>

              <div className="flex justify-end mt-4">
                <Button variant="secondary" size="sm" onClick={clearFilters}>
                  Clear Filters
                </Button>
              </div>
            </>
          )}
        </div>

        {/* Batch Operations Bar */}
        {selectedMembers.length > 0 && (
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-blue-800">
                  {selectedMembers.length} member(s) selected
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={handleBatchDelete}
                  variant="danger"
                  size="sm"
                  className="flex items-center gap-1"
                  disabled={loading}
                >
                  <Trash2 size={14} />
                  Delete Selected
                </Button>
                <Button
                  onClick={() => setSelectedMembers([])}
                  variant="secondary"
                  size="sm"
                >
                  Clear Selection
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Table Controls */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Show</label>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="px-3 py-1 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {itemsPerPageOptions.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            <span className="text-sm text-gray-600">entries</span>
          </div>

          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-gray-700">
              Total Members: {studentRows.length}
            </span>
            <button
              onClick={loadAllData}
              className="p-2 text-gray-500 hover:text-primary-500 transition-colors"
              title="Refresh"
              disabled={loading}
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {/* Members Table */}
        <div className="overflow-x-auto">
          <Table headers={[
            <div key="select" className="flex items-center">
              <input
                type="checkbox"
                checked={currentPageMemberIds.length > 0 && currentPageMemberIds.every((id) => selectedMembers.includes(id))}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedMembers([...new Set(currentItems.map((row) => row.memberId))]);
                  } else {
                    setSelectedMembers([]);
                  }
                }}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
            </div>,
            'Student Name',
            'Grade',
            'Parent Name',
            'Phone',
            'Email',
            'Location',
            'Sport / Activity',
            'Membership Type',
            'Payment Status',
            'Reg. Status',
            'Source',
            'Date Registered',
            'Last Contacted',
            'Actions',
          ]}>
            {currentItems.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <input
                    type="checkbox"
                    checked={selectedMembers.includes(row.memberId)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedMembers([...selectedMembers, row.memberId]);
                      } else {
                        setSelectedMembers(selectedMembers.filter((id) => id !== row.memberId));
                      }
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-semibold">
                      {row.studentName.charAt(0)}
                    </div>
                    <span className="font-medium text-gray-900 whitespace-nowrap">{row.studentName}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-gray-700">{row.grade}</span>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-gray-900 whitespace-nowrap">{row.parentName}</span>
                </TableCell>
                <TableCell>
                  {row.phone ? (
                    <a href={`tel:${row.phone}`} className="text-sm text-blue-600 hover:underline whitespace-nowrap">
                      {formatPhoneLink(row.phone)}
                    </a>
                  ) : (
                    <span className="text-sm text-gray-400">N/A</span>
                  )}
                </TableCell>
                <TableCell>
                  <a href={`mailto:${row.email}`} className="text-sm text-blue-600 hover:underline">
                    {row.email}
                  </a>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-gray-700 whitespace-nowrap">{row.location}</span>
                </TableCell>
                <TableCell>
                  <span className="inline-flex items-center gap-1 text-sm">
                    <span>{getSportIcon(row.sport)}</span>
                    {row.sport}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-gray-700">{row.membershipType}</span>
                </TableCell>
                <TableCell>
                  <StatusBadge status={row.paymentStatus} type="payment" />
                </TableCell>
                <TableCell>
                  <StatusBadge status={row.registrationStatus} type="registration" />
                </TableCell>
                <TableCell>
                  <span className="text-sm text-gray-600">{row.source}</span>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-gray-700 whitespace-nowrap">{formatDate(row.dateRegistered)}</span>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-gray-600 whitespace-nowrap">
                    {row.lastContacted ? formatDate(row.lastContacted) : '—'}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleViewDetails(row.member)}
                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      title="View"
                      disabled={loading}
                    >
                      <Eye size={15} />
                    </button>
                    <button
                      onClick={() => handleEditMember(row.member)}
                      className="p-1.5 text-yellow-600 hover:bg-yellow-50 rounded transition-colors"
                      title="Edit"
                      disabled={loading}
                    >
                      <Edit size={15} />
                    </button>
                    <button
                      onClick={() => openFollowUp(row.member)}
                      className="p-1.5 text-orange-600 hover:bg-orange-50 rounded transition-colors"
                      title="Follow-Up / Notes"
                      disabled={loading}
                    >
                      <StickyNote size={15} />
                    </button>
                    <button
                      onClick={() => handleDeleteMember(row.member)}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                      title="Delete"
                      disabled={loading}
                    >
                      <Trash2 size={15} />
                    </button>
                    <button
                      onClick={() => {
                        setResetPasswordUser(row.member);
                        setIsResetPasswordModalOpen(true);
                        setResetSent(false);
                      }}
                      className="p-1.5 text-purple-600 hover:bg-purple-50 rounded transition-colors"
                      title="Send Password Reset Link"
                      disabled={loading}
                    >
                      <Send size={15} />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </Table>
        </div>

        {/* Empty State */}
        {currentItems.length === 0 && !loading && (
          <div className="text-center py-12">
            <Users size={48} className="mx-auto text-gray-400 mb-3" />
            <p className="text-gray-600 mb-2">
              {searchTerm || hasActiveFilters
                ? 'No members found matching your criteria.'
                : 'No members found.'}
            </p>
            {(searchTerm || hasActiveFilters) && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  clearFilters();
                }}
                className="text-primary-600 hover:text-primary-800 text-sm"
              >
                Clear all filters to see all members
              </button>
            )}
          </div>
        )}

        {/* Loading State */}
        {loading && members.length > 0 && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
            <span className="ml-2 text-gray-600">Processing...</span>
          </div>
        )}

        {/* Pagination */}
        {totalItems > 0 && (
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6 pt-6 border-t border-gray-200">
            <div className="text-sm text-gray-600">
              Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} entries
              {totalPages > 1 && ` — Page ${currentPage} of ${totalPages}`}
            </div>

            {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={goToFirstPage}
                disabled={currentPage === 1}
                className="p-2 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="First Page"
              >
                <ChevronsLeft size={16} />
              </button>

              <button
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
                className="p-2 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Previous Page"
              >
                <ChevronLeft size={16} />
              </button>

              <div className="flex items-center gap-1">
                {getPageNumbers().map(pageNum => (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${pageNum === currentPage
                      ? 'bg-primary-500 text-white'
                      : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                  >
                    {pageNum}
                  </button>
                ))}
              </div>

              <button
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                className="p-2 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Next Page"
              >
                <ChevronRight size={16} />
              </button>

              <button
                onClick={goToLastPage}
                disabled={currentPage === totalPages}
                className="p-2 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Last Page"
              >
                <ChevronsRight size={16} />
              </button>
            </div>
            )}
          </div>
        )}
      </div>

      {/* Site Performance Dashboard */}
      <div id="site-performance-dashboard" className="glass rounded-2xl p-6 mt-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Site Performance Dashboard</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                <th className="py-3 px-4">Location</th>
                <th className="py-3 px-4">Students</th>
                <th className="py-3 px-4">Goal</th>
                <th className="py-3 px-4">% to Goal</th>
                <th className="py-3 px-4">Active</th>
                <th className="py-3 px-4">Pending</th>
                <th className="py-3 px-4">Monthly</th>
                <th className="py-3 px-4">Seasonal</th>
              </tr>
            </thead>
            <tbody>
              {locationStats.map((stat) => (
                <tr key={stat.location} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4 font-medium text-gray-800">{stat.location}</td>
                  <td className="py-3 px-4">{stat.students}</td>
                  <td className="py-3 px-4">{stat.goal}</td>
                  <td className="py-3 px-4">
                    <span className={stat.percentToGoal >= 75 ? 'text-green-600 font-medium' : stat.percentToGoal >= 50 ? 'text-orange-500 font-medium' : 'text-red-500 font-medium'}>
                      {stat.percentToGoal}%
                    </span>
                  </td>
                  <td className="py-3 px-4 text-green-600">{stat.active}</td>
                  <td className="py-3 px-4 text-orange-500">{stat.pending}</td>
                  <td className="py-3 px-4">{stat.monthly}</td>
                  <td className="py-3 px-4">{stat.seasonal}</td>
                </tr>
              ))}
              <tr className="bg-gray-50 font-bold">
                <td className="py-3 px-4">{sitePerformanceTotals.location}</td>
                <td className="py-3 px-4">{sitePerformanceTotals.students}</td>
                <td className="py-3 px-4">{sitePerformanceTotals.goal}</td>
                <td className="py-3 px-4">{sitePerformanceTotals.percentToGoal}%</td>
                <td className="py-3 px-4">{sitePerformanceTotals.active}</td>
                <td className="py-3 px-4">{sitePerformanceTotals.pending}</td>
                <td className="py-3 px-4">{sitePerformanceTotals.monthly}</td>
                <td className="py-3 px-4">{sitePerformanceTotals.seasonal}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <RegistrationByGradeChart
          gradeCounts={gradeDistribution}
          totalStudents={Object.values(gradeDistribution).reduce((a, b) => a + b, 0)}
          locationFilter={gradeChartLocation}
          locations={locations}
          onLocationChange={setGradeChartLocation}
        />
        <MembershipDonutChart breakdown={membershipBreakdown} />
      </div>

      {/* Follow-Ups & Leads */}
      <div className="glass rounded-2xl p-6 mt-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Follow Ups &amp; Leads</h3>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="space-y-4">
            <div className="bg-orange-50 border border-orange-100 rounded-xl p-4">
              <p className="text-sm text-orange-700 font-medium">Follow Ups Needed</p>
              <p className="text-2xl font-bold text-orange-800 mt-1">{followUpsNeededCount} Members</p>
              <button
                type="button"
                onClick={() => setFilters({ ...filters, registrationStatus: 'all' })}
                className="text-xs text-orange-600 hover:underline mt-2"
              >
                View All
              </button>
            </div>
            <div className="bg-green-50 border border-green-100 rounded-xl p-4">
              <p className="text-sm text-green-700 font-medium">Contacted This Week</p>
              <p className="text-2xl font-bold text-green-800 mt-1">{contactedThisWeekCount} Members</p>
            </div>
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
              <p className="text-sm text-blue-700 font-medium">Leads / Interested</p>
              <p className="text-2xl font-bold text-blue-800 mt-1">{leadsCount} Leads</p>
            </div>
          </div>

          <div className="lg:col-span-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs font-semibold text-gray-500 uppercase">
                  <th className="py-2 px-3">Student Name</th>
                  <th className="py-2 px-3">Location</th>
                  <th className="py-2 px-3">Follow Up Date</th>
                  <th className="py-2 px-3">Assigned To</th>
                  <th className="py-2 px-3">Status</th>
                  <th className="py-2 px-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {followUpItems.slice(0, 8).map((item) => (
                  <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-2 px-3 font-medium">{item.studentName}</td>
                    <td className="py-2 px-3">{item.location}</td>
                    <td className="py-2 px-3">
                      {item.followUpDate ? formatDate(item.followUpDate) : '—'}
                    </td>
                    <td className="py-2 px-3">{item.assignedTo}</td>
                    <td className="py-2 px-3">
                      <StatusBadge status={item.status} type="followUp" />
                    </td>
                    <td className="py-2 px-3">
                      <button
                        onClick={() => openFollowUp(item.member)}
                        className="text-primary-600 hover:underline text-xs"
                      >
                        Manage
                      </button>
                    </td>
                  </tr>
                ))}
                {followUpItems.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-6 text-center text-gray-500">
                      No pending follow-ups. Mark members as Follow-Up Needed from the table actions.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {followUpItems.length > 0 && (
              <p className="text-center text-sm text-primary-600 mt-4 cursor-pointer hover:underline">
                View All Follow Ups &amp; Leads ({followUpItems.length})
              </p>
            )}
          </div>
        </div>
      </div>

      {/* View Details Modal */}
      {selectedMember && (
        <Modal
          isOpen={isViewModalOpen}
          onClose={() => {
            setIsViewModalOpen(false);
            setSelectedMember(null);
          }}
          title={`Member Details - ${selectedMember.firstName} ${selectedMember.lastName}`}
          size="xl"
        >
          <div className="space-y-6">
            {/* Header with Member Status */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg ${selectedMember.isPaidMember ? 'bg-green-500' : 'bg-gray-400'
                  }`}>
                  {selectedMember.firstName?.charAt(0) || 'M'}
                </div>
                <div>
                  <h3 className="text-lg font-semibold">{selectedMember.firstName} {selectedMember.lastName}</h3>
                  <p className="text-sm text-gray-600">Member ID: {selectedMember.id}</p>
                </div>
              </div>
              <div className="text-right">

                {selectedMember.isPaidMember ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                    <CheckCircle size={12} />
                    Paid Member
                  </span>
                ) : selectedMember.paymentStatus === 'Active' ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                    <CheckCircle size={12} />
                    Active Member
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium">
                    <Clock size={12} />
                    Free Member
                  </span>
                )}

              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Contact Information */}
              <div className="space-y-4">
                <h4 className="text-md font-semibold text-gray-800 border-b pb-2">Contact Information</h4>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Mail size={16} className="text-gray-400" />
                    <span className="text-sm font-medium">Email:</span>
                    <span className="text-sm text-gray-700">{selectedMember.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone size={16} className="text-gray-400" />
                    <span className="text-sm font-medium">Phone:</span>
                    <span className="text-sm text-gray-700">{selectedMember.phone || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin size={16} className="text-gray-400" />
                    <span className="text-sm font-medium">Location:</span>
                    <span className="text-sm text-gray-700">{selectedMember.location || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Program Information */}
              <div className="space-y-4">
                <h4 className="text-md font-semibold text-gray-800 border-b pb-2">Program Information</h4>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Trophy size={16} className="text-gray-400" />
                    <span className="text-sm font-medium">Sport:</span>
                    <span className="text-sm text-gray-700 flex items-center gap-1">
                      {getSportIcon(selectedMember.sport)} {selectedMember.sport || 'N/A'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Membership Type:</span>
                    <span className="text-sm text-gray-700">{getMembershipTypeLabel(selectedMember) || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Registration Source:</span>
                    <span className="text-sm text-gray-700">
                      {selectedMember.registrationSource === 'web' ? '🌐 Web Registration' : '📱 Mobile App'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Students Information */}
            <div className="space-y-4">
              <h4 className="text-md font-semibold text-gray-800 border-b pb-2">
                Students ({selectedMember.students?.length || 0})
              </h4>
              {selectedMember.students && selectedMember.students.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedMember.students.map((student, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <User size={16} className="text-blue-500" />
                        <span className="font-medium">{student.firstName} {student.lastName}</span>
                      </div>
                      <div className="space-y-1 text-sm text-gray-600">
                        <div><strong>Grade Band:</strong> {student.gradeBand || 'N/A'}</div>
                        {student.uniformTop && (
                          <div><strong>Uniform:</strong> Top: {student.uniformTop}, Bottom: {student.uniformBottom}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No students registered</p>
              )}
            </div>

            {/* Agreements and Consent */}
            <div className="space-y-4">
              <h4 className="text-md font-semibold text-gray-800 border-b pb-2">Agreements & Consent</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className={`p-3 rounded-lg border ${selectedMember.agreeTerms ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
                  }`}>
                  <div className="flex items-center gap-2">
                    {selectedMember.agreeTerms ? (
                      <CheckCircle size={16} className="text-green-600" />
                    ) : (
                      <XCircle size={16} className="text-red-600" />
                    )}
                    <span className="text-sm font-medium">Terms & Conditions</span>
                  </div>
                </div>

                <div className={`p-3 rounded-lg border ${selectedMember.consentText ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
                  }`}>
                  <div className="flex items-center gap-2">
                    {selectedMember.consentText ? (
                      <CheckCircle size={16} className="text-green-600" />
                    ) : (
                      <XCircle size={16} className="text-red-600" />
                    )}
                    <span className="text-sm font-medium">SMS Consent</span>
                  </div>
                </div>

                <div className={`p-3 rounded-lg border ${selectedMember.waiverConsent ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
                  }`}>
                  <div className="flex items-center gap-2">
                    {selectedMember.waiverConsent ? (
                      <CheckCircle size={16} className="text-green-600" />
                    ) : (
                      <XCircle size={16} className="text-red-600" />
                    )}
                    <span className="text-sm font-medium">Waiver Consent</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Follow-Up Tracking */}
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b pb-2">
                <h4 className="text-md font-semibold text-gray-800">Follow-Up Tracking</h4>
                <Button size="sm" onClick={() => openFollowUp(selectedMember)}>
                  <MessageSquare size={14} className="mr-1" />
                  Manage Follow-Up
                </Button>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">Follow-Up Needed:</span>
                  <span className="ml-2">{selectedMember.followUpNeeded ? 'Yes' : 'No'}</span>
                </div>
                <div>
                  <span className="font-medium">Assigned To:</span>
                  <span className="ml-2">{selectedMember.followUpAssignedTo || 'Unassigned'}</span>
                </div>
                <div>
                  <span className="font-medium">Follow-Up Date:</span>
                  <span className="ml-2">{selectedMember.followUpDate ? formatDate(selectedMember.followUpDate) : 'N/A'}</span>
                </div>
                <div>
                  <span className="font-medium">Last Contacted:</span>
                  <span className="ml-2">{selectedMember.lastContacted ? formatDate(selectedMember.lastContacted) : 'N/A'}</span>
                </div>
                {selectedMember.followUpNotes && (
                  <div className="md:col-span-2">
                    <span className="font-medium">Notes:</span>
                    <p className="mt-1 text-gray-700">{selectedMember.followUpNotes}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Registration Details */}
            <div className="space-y-4">
              <h4 className="text-md font-semibold text-gray-800 border-b pb-2">Registration Details</h4>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Registered:</span>
                    <span className="ml-2 text-gray-700">{formatDate(selectedMember.createdAt)}</span>
                  </div>
                  <div>
                    <span className="font-medium">UID:</span>
                    <span className="ml-2 text-gray-700 font-mono text-xs">{selectedMember.uid || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Follow-Up Modal */}
      {followUpMember && (
        <Modal
          isOpen={isFollowUpModalOpen}
          onClose={() => {
            setIsFollowUpModalOpen(false);
            setFollowUpMember(null);
          }}
          title={`Follow-Up - ${followUpMember.firstName} ${followUpMember.lastName}`}
          size="lg"
        >
          <FollowUpModal
            member={followUpMember}
            staffOptions={staffList}
            onSave={handleSaveFollowUp}
            onClose={() => {
              setIsFollowUpModalOpen(false);
              setFollowUpMember(null);
            }}
            loading={loading}
          />
        </Modal>
      )}

      {/* Edit Member Modal */}
      {editingMember && (
        <Modal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setEditingMember(null);
          }}
          title={`Edit Member - ${editingMember.firstName} ${editingMember.lastName}`}
          size="xl"
        >
          <EditMemberForm
            member={editingMember}
            onSave={handleSaveEdit}
            onCancel={() => {
              setIsEditModalOpen(false);
              setEditingMember(null);
            }}
            loading={loading}
            isNewMember={false}
          />
        </Modal>
      )}

      {/* Add New Member Modal */}
      <Modal
        isOpen={isAddMemberModalOpen}
        onClose={() => setIsAddMemberModalOpen(false)}
        title="Add New Member"
        size="xl"
      >
        <AddMemberForm
          onSave={handleAddNewMember}
          onCancel={() => setIsAddMemberModalOpen(false)}
          setLoading={setLoading}
          loading={loading}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.isOpen && (
        <Modal
          isOpen={deleteConfirmation.isOpen}
          onClose={() => setDeleteConfirmation({ isOpen: false, member: null })}
          title="Confirm Deletion"
          size="md"
        >
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-red-50 rounded-lg">
              <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
                <AlertTriangle className="text-white" size={20} />
              </div>
              <div>
                <h4 className="font-semibold text-red-900">Delete Member</h4>
                <p className="text-sm text-red-700">This action cannot be undone</p>
              </div>
            </div>

            <div className="p-4 border border-gray-200 rounded-lg">
              <h5 className="font-medium mb-2">Member to be deleted:</h5>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gray-400 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                  {deleteConfirmation.member?.firstName?.charAt(0)}
                </div>
                <div>
                  <p className="font-medium">
                    {deleteConfirmation.member?.firstName} {deleteConfirmation.member?.lastName}
                  </p>
                  <p className="text-sm text-gray-600">{deleteConfirmation.member?.email}</p>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h5 className="font-medium text-yellow-800 mb-2 flex items-center gap-1">
                <AlertTriangle size={16} />
                Warning
              </h5>
              <ul className="text-sm text-yellow-700 space-y-1">
                <li>• Member will be removed from the members database</li>
                <li>• Their Firebase Auth account will be deleted</li>
                <li>• Students will be removed from all team rosters</li>
                <li>• All related data (payments, notifications) will be deleted</li>
                <li>• This action is permanent and cannot be undone</li>
              </ul>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                onClick={() => setDeleteConfirmation({ isOpen: false, member: null })}
                variant="secondary"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDelete}
                variant="danger"
                disabled={loading}
                className="flex items-center gap-2"
              >
                <Trash2 size={16} />
                {loading ? 'Deleting...' : 'Delete Member'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Email Templates Modal */}
      {showEmailTemplates && (
        <Modal
          isOpen={true}
          onClose={() => setShowEmailTemplates(false)}
          title="Email Template Management"
          size="large"
        >
          <EmailTemplates />
        </Modal>
      )}

      {/* Reset Password Modal */}
      <Modal
        isOpen={isResetPasswordModalOpen}
        onClose={() => {
          setIsResetPasswordModalOpen(false);
          setResetPasswordUser(null);
          setResetSent(false);
        }}
        title="Reset Password"
        size="sm"
      >
        {resetPasswordUser && (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <Mail className="h-5 w-5 text-blue-400 mt-0.5 shrink-0" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">Send Password Reset Link</h3>
                  <p className="text-sm text-blue-700 mt-1">
                    A password reset email will be sent to:
                  </p>
                  <p className="text-sm font-semibold text-blue-800 mt-1 break-all">
                    {resetPasswordUser.email}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    User: {resetPasswordUser.firstName} {resetPasswordUser.lastName}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <p className="text-sm text-gray-600">
                Firebase will send an email with a password reset link. The user can click the link to set a new password. This works for all user roles without restrictions.
              </p>
            </div>

            {resetSent ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <p className="text-green-800 font-medium">Reset link sent successfully!</p>
                <p className="text-green-600 text-sm mt-1">The user will receive the email shortly.</p>
              </div>
            ) : (
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setIsResetPasswordModalOpen(false);
                    setResetPasswordUser(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={() => handleSendResetLink(resetPasswordUser)}
                  disabled={resetSending}
                >
                  {resetSending ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Members;
